import React from 'react';
import { X, Calendar, Clock, User, UserCheck, FileText, Phone, Mail, MapPin, Edit, Trash2 } from 'lucide-react';
import { Appointment } from '../../lib/api';

interface AppointmentDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  appointment: Appointment | null;
  onEdit?: () => void;
  onCancel?: () => void;
}

const AppointmentDetailsModal: React.FC<AppointmentDetailsModalProps> = ({
  isOpen,
  onClose,
  appointment,
  onEdit,
  onCancel
}) => {
  if (!isOpen || !appointment) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return timeString;
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'SCHEDULED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'NO_SHOW':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'IN_PROGRESS':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'CONFIRMED':
      case 'COMPLETED':
        return '✓';
      case 'SCHEDULED':
        return '📅';
      case 'IN_PROGRESS':
        return '⏳';
      case 'CANCELLED':
      case 'NO_SHOW':
        return '✕';
      default:
        return '❓';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Calendar className="h-6 w-6 mr-2 text-blue-600" />
              Appointment Details
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {formatDate(appointment.appointmentDate)} at {formatTime(appointment.appointmentTime)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Status:</span>
              <span className={`px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(appointment.status)}`}>
                {getStatusIcon(appointment.status)} {appointment.status.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Patient Information */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
              <User className="h-5 w-5 mr-2 text-blue-600" />
              Patient Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">Name</label>
                <p className="mt-1 text-gray-900">
                  {appointment.patient?.user?.firstName && appointment.patient?.user?.lastName ? (
                    `${appointment.patient.user.firstName} ${appointment.patient.user.lastName}`
                  ) : (
                    <span className="text-gray-500 italic">Name not available</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Patient Number</label>
                <p className="mt-1 text-gray-900">
                  {appointment.patient?.patientNumber || (
                    <span className="text-gray-500 italic">Not assigned</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Email</label>
                <p className="mt-1 text-gray-900 flex items-center">
                  <Mail className="h-4 w-4 mr-1 text-gray-400" />
                  {appointment.patient?.user?.email ? (
                    appointment.patient.user.email
                  ) : (
                    <span className="text-gray-500 italic">Email not available</span>
                  )}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Phone</label>
                <p className="mt-1 text-gray-900 flex items-center">
                  <Phone className="h-4 w-4 mr-1 text-gray-400" />
                  {appointment.patient?.user?.phone ? (
                    appointment.patient.user.phone
                  ) : (
                    <span className="text-gray-500 italic">Phone not available</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Doctor Information */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
              <UserCheck className="h-5 w-5 mr-2 text-green-600" />
              Doctor Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">Name</label>
                <p className="text-gray-900">
                  Dr. {(appointment.doctor?.user?.firstName || '')} {(appointment.doctor?.user?.lastName || '')}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">License Number</label>
                <p className="text-gray-900">{appointment.doctor?.licenseNumber || ''}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Specialization</label>
                <p className="text-gray-900">{appointment.doctor?.specialization || ''}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Department</label>
                <p className="text-gray-900">{appointment.doctor?.department || ''}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Email</label>
                <p className="text-gray-900 flex items-center">
                  <Mail className="h-4 w-4 mr-1 text-gray-400" />
                  {appointment.doctor?.user?.email || ''}
                </p>
              </div>
            </div>
          </div>

          {/* Appointment Details */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
              <Calendar className="h-5 w-5 mr-2 text-purple-600" />
              Appointment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-600">Date</label>
                <p className="text-gray-900 flex items-center">
                  <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                  {formatDate(appointment.appointmentDate)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Time</label>
                <p className="text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 mr-1 text-gray-400" />
                  {formatTime(appointment.appointmentTime)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Created</label>
                <p className="text-gray-900">
                  {new Date(appointment.createdAt).toLocaleDateString()} at{' '}
                  {new Date(appointment.createdAt).toLocaleTimeString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-600">Last Updated</label>
                <p className="text-gray-900">
                  {new Date(appointment.updatedAt).toLocaleDateString()} at{' '}
                  {new Date(appointment.updatedAt).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>

          {/* Notes */}
          {appointment.notes && (
            <div className="border border-gray-200 rounded-lg p-4">
              <h3 className="flex items-center text-lg font-medium text-gray-900 mb-3">
                <FileText className="h-5 w-5 mr-2 text-orange-600" />
                Notes
              </h3>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-gray-700 whitespace-pre-wrap">{appointment.notes}</p>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h4>
            <div className="flex flex-wrap gap-2">
              {onEdit && appointment.status !== 'COMPLETED' && appointment.status !== 'CANCELLED' && (
                <button
                  onClick={onEdit}
                  className="flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Edit
                </button>
              )}
              
              {onCancel && appointment.status !== 'COMPLETED' && appointment.status !== 'CANCELLED' && (
                <button
                  onClick={onCancel}
                  className="flex items-center px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Cancel
                </button>
              )}

              <button
                onClick={() => window.print()}
                className="flex items-center px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
              >
                🖨️ Print
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppointmentDetailsModal; 